// A workflow's trigger and tool policy arrive as untyped JSON (ocs-core `Trigger` and
// `ToolPolicy` in domain/workflow.rs); parse them once here into what the pages show.
import { messages } from "../i18n";

export type Trigger =
  | { kind: "manual" }
  | { kind: "cron"; expression: string }
  | { kind: "interval"; seconds: number }
  | { kind: "ticket"; jql: string }
  | { kind: "unknown"; label: string };

const record = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;

export function parseTrigger(raw: unknown): Trigger {
  const t = record(raw);
  switch (t?.kind) {
    case "manual":
      return { kind: "manual" };
    case "cron":
      return { kind: "cron", expression: typeof t.expression === "string" ? t.expression : "" };
    case "interval":
      return { kind: "interval", seconds: typeof t.seconds === "number" ? t.seconds : 0 };
    case "ticket":
      return { kind: "ticket", jql: typeof t.jql === "string" ? t.jql : "" };
    default:
      return { kind: "unknown", label: typeof t?.kind === "string" ? t.kind : "—" };
  }
}

function every(seconds: number): string {
  const t = messages().workloads.workflow.trigger;
  if (seconds % 86_400 === 0) return seconds === 86_400 ? t.daily : t.everyDays(seconds / 86_400);
  if (seconds % 3600 === 0) return seconds === 3600 ? t.hourly : t.everyHours(seconds / 3600);
  if (seconds % 60 === 0) return t.everyMinutes(seconds / 60);
  return t.everySeconds(seconds);
}

/** "Manual", "Cron · 0 2 * * *", "Interval · every 15 min", "Ticket · project = OPS". */
export function triggerLabel(t: Trigger): string {
  const label = messages().workloads.workflow.trigger;
  switch (t.kind) {
    case "manual":
      return label.manual;
    case "cron":
      return `${label.cron} · ${t.expression}`;
    case "interval":
      return `${label.interval} · ${every(t.seconds)}`;
    case "ticket":
      return `${label.ticket} · ${t.jql}`;
    case "unknown":
      return t.label;
  }
}

/** What started a run (ocs-core `TriggerDetail`): "Manual", "Cron", "Interval", "Ticket OPS-12". */
export function runTriggerLabel(raw: unknown): string {
  const t = record(raw);
  const label = messages().workloads.workflow.trigger;
  switch (t?.kind) {
    case "manual":
      return label.manual;
    case "cron":
      return label.cron;
    case "interval":
      return label.interval;
    case "ticket":
      return typeof t.key === "string" ? label.ticketKey(t.key) : label.ticket;
    default:
      return typeof t?.kind === "string" ? t.kind : "—";
  }
}

/** One action a run's capability token may name: `github.pulls.list` on `Izrayl/icykofi`. */
export interface PolicyAction {
  action: string;
  scope: string;
  requiresApproval: boolean;
}

function facet(value: unknown): string {
  if (Array.isArray(value)) return value.map(facet).join(", ");
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? messages().workloads.workflow.scope.yes : messages().workloads.workflow.scope.no;
  return JSON.stringify(value);
}

/** The policy's grants, one row per action; resource facets (`repos`, `projects`…) become the scope. */
export function policyActions(raw: unknown): PolicyAction[] {
  const caps = record(raw)?.capabilities;
  if (!Array.isArray(caps)) return [];
  return caps.flatMap((c) => {
    const cap = record(c);
    if (!cap || typeof cap.tool !== "string" || !Array.isArray(cap.actions)) return [];
    const { tool, actions, requires_approval: approval, ...resources } = cap;
    const scope = Object.entries(resources)
      .map(([k, v]) => (k === "readonly" && v === true ? messages().workloads.workflow.scope.readOnly : facet(v)))
      .filter(Boolean)
      .join(" · ");
    return actions
      .filter((a): a is string => typeof a === "string")
      .map((a) => ({ action: `${tool}.${a}`, scope, requiresApproval: approval === true }));
  });
}
